/**
 * RevenueSourcesCard — third-party revenue source capture + reconciliation.
 *
 * Restaurant and retail BI claims frequently show a systematic gap
 * between bank operating deposits and 1065 gross receipts (~15-30%).
 * The usual culprits are delivery-app processors (DoorDash, Uber
 * Eats, Grubhub), secondary merchant accounts (Square, Clover, Toast),
 * or sales-tax-reported gross that exceeds what landed in the bank.
 *
 * Phase 1 (shipped): processor/sales-tax metadata + client email request.
 * Phase 2 (this card, when statements + reconciliation are present):
 *   - auto-flip processor applies/status from detected platforms
 *   - "Run reconciliation" button trigger
 *   - Per-year reconciliation summary panel (gap, platforms, explained %,
 *     residual, ACH matches, zero-activity BI corroboration)
 */

import React, { useState, useMemo } from 'react';
import {
  Store,
  Receipt,
  Send,
  Check,
  AlertCircle,
  Plus,
  X,
  RefreshCw,
  CheckCircle2,
  Activity,
  Flame,
} from 'lucide-react';
import api from '../../../utils/api';

export interface RevenueProcessor {
  id: string;
  type: string;          // canonical slug (e.g. "doordash")
  label: string;         // display name
  applies: boolean;      // client confirmed this processor is used
  status: 'pending' | 'requested' | 'received' | 'analyzed';
  notes?: string;
  custom_label?: string; // for type === "other"
}

export interface RevenueSourcesData {
  processors: RevenueProcessor[];
  sales_tax: {
    state: string;
    account_number: string;
    status: 'pending' | 'requested' | 'received' | 'analyzed';
    filing_cadence?: 'monthly' | 'quarterly' | 'annual' | '';
    notes?: string;
  };
  last_requested_at?: string;
  last_request_id?: string;
}

/** Parsed merchant statement envelope as stored under
 *  `draft.data.revenue_source_statements[]` (see backend
 *  `app.models.revenue_source.MerchantStatementEnvelope`). */
export interface MerchantStatementEnvelope {
  file_id: string;
  filename: string;
  s3_key?: string;
  uploaded_at: string;
  parsed: {
    processor_name: string;
    account_holder?: string;
    statement_period_year?: number;
    statement_period_month?: number;
    zero_activity_flag?: boolean;
    narrative_notes?: string;
    platform_breakdown?: { platform: string; gross_sales: number }[];
    settlement_summary?: { total_gross_sales: number; total_net_payout: number };
    confidence?: number;
  };
  reconciled?: boolean;
  reconciliation_run_at?: string | null;
}

/** Reconciliation summary as persisted under
 *  `draft.data.revenue_source_reconciliation` (see backend
 *  `merchant_reconciliation_service.ReconciliationSummary.to_dict()`). */
export interface ReconciliationSummary {
  claim_id: string;
  run_at: string;
  tolerances_used: Record<string, number>;
  years: Array<{
    year: number;
    tax_1065_gross_receipts?: number | null;
    bank_operating_deposits?: number | null;
    bank_1065_gap?: number | null;
    merchant_statement_gross_sales: number;
    merchant_statement_net_payout: number;
    merchant_statement_fees: number;
    residual_unexplained?: number | null;
    explained_pct?: number | null;
    platforms_detected: string[];
    zero_activity_months: string[];
    statements_count: number;
    ach_matched_count: number;
    ach_unmatched_count: number;
    parse_warnings: string[];
  }>;
  platforms_detected_all_years: string[];
  bi_event_evidence: Array<{
    year_month?: string;
    processor?: string;
    narrative?: string;
    file?: string;
  }>;
  statements_analyzed: number;
  statements_skipped: number;
}

interface Props {
  value?: RevenueSourcesData;
  onChange: (next: RevenueSourcesData) => void;
  claimId?: string;
  clientEmail?: string;
  /** Parsed statements from the wizard uploader (phase 2 integration). */
  statements?: MerchantStatementEnvelope[];
  /** Existing reconciliation summary (phase 2 integration). */
  reconciliation?: ReconciliationSummary | null;
  /** Called after a successful reconcile so the parent can refresh draft state. */
  onReconciled?: (summary: ReconciliationSummary) => void;
}

const DEFAULT_PROCESSORS: Omit<RevenueProcessor, 'applies' | 'status'>[] = [
  { id: 'doordash',  type: 'doordash',  label: 'DoorDash' },
  { id: 'ubereats',  type: 'ubereats',  label: 'Uber Eats' },
  { id: 'grubhub',   type: 'grubhub',   label: 'Grubhub' },
  { id: 'square',    type: 'square',    label: 'Square' },
  { id: 'toast',     type: 'toast',     label: 'Toast' },
  { id: 'clover',    type: 'clover',    label: 'Clover' },
  { id: 'stripe',    type: 'stripe',    label: 'Stripe' },
  { id: 'paypal',    type: 'paypal',    label: 'PayPal' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA',
  'ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK',
  'OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const STATUS_LABELS: Record<RevenueProcessor['status'], string> = {
  pending:   'Not requested',
  requested: 'Requested',
  received:  'Received',
  analyzed:  'Analyzed',
};

const STATUS_STYLES: Record<RevenueProcessor['status'], React.CSSProperties> = {
  pending:   { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' },
  requested: { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  received:  { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  analyzed:  { background: 'transparent', color: 'var(--color-verified)' },
};

export default function RevenueSourcesCard({
  value,
  onChange,
  claimId,
  clientEmail,
  statements = [],
  reconciliation = null,
  onReconciled,
}: Props) {
  // Detected-platform set aggregated across all parsed statements.  Used
  // to auto-flip processor rows from the 8-default seed to "applies=true"
  // with status="received" when the matching statement arrives.
  const detectedPlatforms = useMemo(() => {
    const set = new Set<string>();
    for (const env of statements) {
      for (const row of env.parsed?.platform_breakdown || []) {
        if (row.platform) set.add(row.platform.trim());
      }
      // Single-platform statements without a breakdown — fall back to
      // the processor name itself.
      if ((!env.parsed?.platform_breakdown || env.parsed.platform_breakdown.length === 0)
          && env.parsed?.processor_name) {
        set.add(env.parsed.processor_name.trim());
      }
    }
    return set;
  }, [statements]);

  // Hydrate with defaults — every known processor gets a row, client just
  // toggles applies=true on the ones they actually use.  When a matching
  // statement has been parsed, auto-flip applies=true and status.
  const hydrated = useMemo<RevenueSourcesData>(() => {
    const existing = value?.processors || [];
    const byType = new Map(existing.map(p => [p.type, p]));
    const merged: RevenueProcessor[] = DEFAULT_PROCESSORS.map(def => {
      const prev = byType.get(def.type);
      const base = prev ?? { ...def, applies: false, status: 'pending' as const };
      // Auto-flip if we have a parsed statement for this platform.
      const detected = Array.from(detectedPlatforms).some((p: string) => {
        const norm = String(p).toLowerCase().replace(/[^a-z0-9]/g, '');
        return norm.includes(def.type) || def.type.includes(norm);
      });
      if (detected) {
        const nextStatus: RevenueProcessor['status'] =
          reconciliation ? 'analyzed' : (base.status === 'pending' ? 'received' : base.status);
        return { ...base, applies: true, status: nextStatus };
      }
      return base;
    });
    // Preserve any user-added "Other" entries.
    existing.filter(p => !DEFAULT_PROCESSORS.some(d => d.type === p.type)).forEach(p => merged.push(p));
    return {
      processors: merged,
      sales_tax: value?.sales_tax ?? { state: '', account_number: '', status: 'pending', filing_cadence: '', notes: '' },
      last_requested_at: value?.last_requested_at,
      last_request_id: value?.last_request_id,
    };
  }, [value, detectedPlatforms, reconciliation]);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [emailOverride, setEmailOverride] = useState('');

  const [reconcileRunning, setReconcileRunning] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  const updateProcessor = (idx: number, patch: Partial<RevenueProcessor>) => {
    const next = { ...hydrated, processors: hydrated.processors.map((p, i) => i === idx ? { ...p, ...patch } : p) };
    onChange(next);
  };

  const updateSalesTax = (patch: Partial<RevenueSourcesData['sales_tax']>) => {
    onChange({ ...hydrated, sales_tax: { ...hydrated.sales_tax, ...patch } });
  };

  const addCustomProcessor = () => {
    const id = `other-${Date.now()}`;
    onChange({
      ...hydrated,
      processors: [...hydrated.processors, {
        id, type: id, label: 'Other', applies: true, status: 'pending', custom_label: '',
      }],
    });
  };

  const removeProcessor = (idx: number) => {
    onChange({ ...hydrated, processors: hydrated.processors.filter((_, i) => i !== idx) });
  };

  const appliesCount = hydrated.processors.filter(p => p.applies).length;
  const pendingCount = hydrated.processors.filter(p => p.applies && p.status === 'pending').length
    + (hydrated.sales_tax.state && hydrated.sales_tax.status === 'pending' ? 1 : 0);

  // Reconciliation trigger — hits the on-demand endpoint we added in
  // backend Phase 2.3 and bubbles the summary back to the parent.
  const runReconciliation = async () => {
    setReconcileError(null);
    if (!claimId) { setReconcileError('Claim ID missing — save draft first'); return; }
    if (statements.length === 0) { setReconcileError('Upload at least one merchant statement first'); return; }
    setReconcileRunning(true);
    try {
      const res: any = await api.post(
        `/api/v1/upload-savefirst/reconcile-revenue-sources/${claimId}`,
        {},
        { showErrorToast: false },
      );
      if (res?.summary) {
        onReconciled?.(res.summary);
      }
    } catch (err: any) {
      setReconcileError(err?.message || 'Reconciliation failed');
    } finally {
      setReconcileRunning(false);
    }
  };

  const totalStatementsGross = statements.reduce(
    (s, e) => s + (e.parsed?.settlement_summary?.total_gross_sales || 0),
    0,
  );
  const fmtUsd = (n: number | null | undefined) => {
    if (n === null || n === undefined) return '—';
    if (n === 0) return '$0';
    const neg = n < 0;
    const abs = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
    return neg ? `($${abs})` : `$${abs}`;
  };

  const sendClientRequest = async () => {
    setSendError(null);
    setSendSuccess(false);
    const email = emailOverride || clientEmail || '';
    if (!email) { setSendError('Client email required'); return; }
    if (!claimId) { setSendError('Claim ID missing — save draft first'); return; }

    const selectedProcessors = hydrated.processors.filter(p => p.applies);
    if (selectedProcessors.length === 0 && !hydrated.sales_tax.state) {
      setSendError('Select at least one processor or enter a sales tax state');
      return;
    }

    setSending(true);
    try {
      const questions = selectedProcessors.map(p => ({
        transaction_id: p.id,
        date: new Date().toISOString().slice(0, 10),
        description: `${p.custom_label || p.label} statements`,
        amount: 0,
        current_category: 'Revenue Source',
        question: `Please provide year-end 1099-K or monthly settlement statements from ${p.custom_label || p.label} for the claim period and 3 prior years.`,
        options: ['Will provide', 'Do not use this processor', 'Cannot locate'],
      }));
      if (hydrated.sales_tax.state) {
        questions.push({
          transaction_id: 'sales-tax',
          date: new Date().toISOString().slice(0, 10),
          description: `${hydrated.sales_tax.state} sales tax filings`,
          amount: 0,
          current_category: 'Sales Tax',
          question: `Please provide ${hydrated.sales_tax.state} sales tax filings (all periods) for the claim period and 3 prior years${hydrated.sales_tax.account_number ? ` (account # ${hydrated.sales_tax.account_number})` : ''}.`,
          options: ['Will provide', 'Not registered', 'Cannot locate'],
        });
      }

      const createRes: any = await api.post(
        '/api/v1/clarifications/create',
        { report_id: claimId, claimant_email: email, transactions: questions },
        { showErrorToast: false }
      );
      const request_id = createRes?.request_id;
      if (!request_id) throw new Error('Backend did not return request_id');
      await api.post(`/api/v1/clarifications/send/${request_id}`, {}, { showErrorToast: false });

      // Mark all selected items as requested.
      const nowIso = new Date().toISOString();
      const nextProcessors = hydrated.processors.map(p =>
        p.applies && p.status === 'pending' ? { ...p, status: 'requested' as const } : p
      );
      const nextSalesTax = hydrated.sales_tax.state && hydrated.sales_tax.status === 'pending'
        ? { ...hydrated.sales_tax, status: 'requested' as const }
        : hydrated.sales_tax;
      onChange({
        ...hydrated,
        processors: nextProcessors,
        sales_tax: nextSalesTax,
        last_requested_at: nowIso,
        last_request_id: request_id,
      });
      setSendSuccess(true);
    } catch (err: any) {
      setSendError(err?.message || 'Failed to send client request');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
    >
      <div
        className="px-6 py-4"
        style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border)' }}
      >
        <h3 className="cs-h3 flex items-center gap-2">
          <Store className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
          Revenue Sources
          {appliesCount > 0 && (
            <span className="ml-2 cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
              {appliesCount} selected
              {pendingCount > 0 && ` · ${pendingCount} pending request`}
            </span>
          )}
        </h3>
        <p className="cs-meta mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          Third-party processors and sales tax. Use this when Bank vs 1065 variance suggests missing deposit sources.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Statements + reconciliation summary (Phase 2) */}
        {statements.length > 0 && (
          <div className="rounded-lg p-4" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  <Activity className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  Statements parsed
                </div>
                <div className="cs-meta mt-1">
                  {statements.length} statement{statements.length === 1 ? '' : 's'} ·{' '}
                  {fmtUsd(totalStatementsGross)} total gross sales ·{' '}
                  {Array.from(detectedPlatforms).length} platform{detectedPlatforms.size === 1 ? '' : 's'} detected
                </div>
                <div className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
                  Platforms: {Array.from(detectedPlatforms).sort().join(', ') || '—'}
                </div>
              </div>
              {/* Reconciliation runs automatically when merchant statements
                  land.  This button is a manual re-run (e.g. after changing
                  match tolerances or re-uploading a corrected statement). */}
              <button
                type="button"
                onClick={runReconciliation}
                disabled={reconcileRunning || !claimId}
                className="cs-meta inline-flex items-center gap-2 px-3 py-1.5 rounded disabled:cursor-not-allowed"
                style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', fontWeight: 500 }}
                title="Reconciliation runs automatically on upload. Click to re-run manually."
              >
                <RefreshCw className={`w-3.5 h-3.5 ${reconcileRunning ? 'animate-spin' : ''}`} />
                {reconcileRunning ? 'Reconciling…' : 'Re-run reconciliation'}
              </button>
            </div>
            {reconcileError && (
              <div className="mt-2 cs-meta flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}>
                <AlertCircle className="w-3.5 h-3.5" />
                {reconcileError}
              </div>
            )}

            {reconciliation && reconciliation.years.length > 0 && (
              <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
                <div className="cs-body mb-2 flex items-center gap-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--color-verified)' }} />
                  Reconciliation results
                  <span className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
                    · run {new Date(reconciliation.run_at).toLocaleString()}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left cs-label">
                        <th className="py-1 pr-3">Year</th>
                        <th className="py-1 pr-3 text-right">1065 gross</th>
                        <th className="py-1 pr-3 text-right">Bank op.</th>
                        <th className="py-1 pr-3 text-right">Gap</th>
                        <th className="py-1 pr-3 text-right">Merchant gross</th>
                        <th className="py-1 pr-3 text-right">Explained</th>
                        <th className="py-1 pr-3 text-right">Residual</th>
                        <th className="py-1 pr-3 text-right">ACH match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconciliation.years.map((y) => (
                        <tr key={y.year} style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                          <td className="py-1 pr-3 cs-body" style={{ fontWeight: 600 }}>{y.year}</td>
                          <td className="py-1 pr-3 text-right cs-figure">{fmtUsd(y.tax_1065_gross_receipts)}</td>
                          <td className="py-1 pr-3 text-right cs-figure">{fmtUsd(y.bank_operating_deposits)}</td>
                          <td className="py-1 pr-3 text-right cs-figure">{fmtUsd(y.bank_1065_gap)}</td>
                          <td className="py-1 pr-3 text-right cs-figure">{fmtUsd(y.merchant_statement_gross_sales)}</td>
                          <td className="py-1 pr-3 text-right cs-figure">
                            {y.explained_pct != null ? `${y.explained_pct.toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-1 pr-3 text-right cs-figure">{fmtUsd(y.residual_unexplained)}</td>
                          <td className="py-1 pr-3 text-right cs-body">
                            {y.ach_matched_count}/{y.ach_matched_count + y.ach_unmatched_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {reconciliation.bi_event_evidence.length > 0 && (
                  <div className="mt-3 rounded-lg p-3" style={{ border: '1px solid var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}>
                    <div className="cs-meta flex items-center gap-1.5 mb-2.5" style={{ fontWeight: 600, color: 'var(--color-needs-human-text)' }}>
                      <Flame className="w-3.5 h-3.5" />
                      BI event corroboration — zero-activity statements
                    </div>
                    <ul className="space-y-2.5">
                      {reconciliation.bi_event_evidence.map((ev, i) => (
                        <li key={i} className="pl-2.5" style={{ borderLeft: '2px solid var(--color-needs-human-tint-bd)' }}>
                          <div className="cs-label flex items-center gap-1.5">
                            <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)' }}>{ev.year_month || ev.file}</span>
                            {ev.processor && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({ev.processor})</span>}
                          </div>
                          {ev.narrative && (
                            <p className="cs-meta mt-1 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{ev.narrative}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Processor grid */}
        <div>
          <h4 className="cs-body mb-3" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Third-party processors</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {hydrated.processors.map((p, idx) => {
              const isCustom = !DEFAULT_PROCESSORS.some(d => d.type === p.type);
              return (
                <div
                  key={p.id}
                  className="rounded-lg p-3 transition-colors"
                  style={
                    p.applies
                      ? { border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }
                      : { border: '1px solid var(--color-border-sub)', background: 'var(--color-bg-base)' }
                  }
                >
                  <div className="flex items-start gap-3">
                    <label className="flex items-center cursor-pointer mt-0.5">
                      <input
                        type="checkbox"
                        checked={p.applies}
                        onChange={(e) => updateProcessor(idx, { applies: e.target.checked })}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: 'var(--color-brand)' }}
                      />
                    </label>
                    <div className="flex-1 min-w-0">
                      {isCustom ? (
                        <input
                          type="text"
                          value={p.custom_label || ''}
                          onChange={(e) => updateProcessor(idx, { custom_label: e.target.value })}
                          placeholder="Processor name"
                          className="cs-body w-full bg-transparent border-b border-dashed focus:outline-none"
                          style={{ fontWeight: 500, color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}
                        />
                      ) : (
                        <div className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{p.label}</div>
                      )}
                      {p.applies && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <select
                            value={p.status}
                            onChange={(e) => updateProcessor(idx, { status: e.target.value as RevenueProcessor['status'] })}
                            className="cs-chip rounded-full px-2 py-0.5 border-0"
                            style={STATUS_STYLES[p.status]}
                          >
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={p.notes || ''}
                            onChange={(e) => updateProcessor(idx, { notes: e.target.value })}
                            placeholder="Notes (account #, settlement bank, …)"
                            className="cs-meta flex-1 px-2 py-1 rounded focus:outline-none min-w-0"
                            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                          />
                          {isCustom && (
                            <button
                              type="button"
                              onClick={() => removeProcessor(idx)}
                              style={{ color: 'var(--color-text-dim)' }}
                              aria-label="Remove processor"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addCustomProcessor}
            className="cs-body mt-3 flex items-center gap-1"
            style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}
          >
            <Plus className="w-4 h-4" /> Add another processor
          </button>
        </div>

        {/* Sales tax */}
        <div className="pt-5" style={{ borderTop: '1px solid var(--color-border)' }}>
          <h4 className="cs-body mb-3 flex items-center gap-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            <Receipt className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
            State sales tax
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="cs-label block mb-1">State</label>
              <select
                value={hydrated.sales_tax.state}
                onChange={(e) => updateSalesTax({ state: e.target.value })}
                className="cs-body w-full px-2 py-1.5 rounded focus:outline-none"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                <option value="">—</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="cs-label block mb-1">Sales tax account #</label>
              <input
                type="text"
                value={hydrated.sales_tax.account_number}
                onChange={(e) => updateSalesTax({ account_number: e.target.value })}
                placeholder="If known"
                className="cs-body w-full px-2 py-1.5 rounded focus:outline-none"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              />
            </div>
            <div>
              <label className="cs-label block mb-1">Filing cadence</label>
              <select
                value={hydrated.sales_tax.filing_cadence || ''}
                onChange={(e) => updateSalesTax({ filing_cadence: e.target.value as any })}
                className="cs-body w-full px-2 py-1.5 rounded focus:outline-none"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              >
                <option value="">—</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>
          {hydrated.sales_tax.state && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <select
                value={hydrated.sales_tax.status}
                onChange={(e) => updateSalesTax({ status: e.target.value as any })}
                className="cs-chip rounded-full px-2 py-0.5 border-0"
                style={STATUS_STYLES[hydrated.sales_tax.status]}
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <input
                type="text"
                value={hydrated.sales_tax.notes || ''}
                onChange={(e) => updateSalesTax({ notes: e.target.value })}
                placeholder="Notes (portal URL, login contact, …)"
                className="cs-meta flex-1 px-2 py-1 rounded focus:outline-none min-w-[200px]"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              />
            </div>
          )}
        </div>

        {/* Client request */}
        <div className="pt-5" style={{ borderTop: '1px solid var(--color-border)' }}>
          <h4 className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Request from client</h4>
          <p className="cs-meta mb-3">
            Sends a single email asking for statements/filings for every processor and sales tax state marked above.
            Items flip to "Requested" once the email goes out.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="email"
              value={emailOverride || clientEmail || ''}
              onChange={(e) => setEmailOverride(e.target.value)}
              placeholder="client@example.com"
              className="cs-body flex-1 min-w-[240px] px-3 py-2 rounded focus:outline-none"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            />
            <button
              type="button"
              onClick={sendClientRequest}
              disabled={sending}
              className="cs-body inline-flex items-center gap-2 px-4 py-2 rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending…' : 'Send client request'}
            </button>
          </div>
          {sendError && (
            <div className="mt-2 cs-meta flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}>
              <AlertCircle className="w-3.5 h-3.5" />
              {sendError}
            </div>
          )}
          {sendSuccess && (
            <div className="mt-2 cs-meta flex items-center gap-1.5" style={{ color: 'var(--color-verified)' }}>
              <Check className="w-3.5 h-3.5" />
              Request sent. Status updated to "Requested".
            </div>
          )}
          {hydrated.last_requested_at && !sendSuccess && (
            <div className="mt-2 cs-meta" style={{ color: 'var(--color-text-dim)' }}>
              Last request: {new Date(hydrated.last_requested_at).toLocaleString()}
            </div>
          )}
        </div>

        {/* Hand-off guidance */}
        <div
          className="pt-4 cs-meta -mx-6 -mb-6 px-6 py-4 rounded-b-xl"
          style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-base)' }}
        >
          <strong style={{ color: 'var(--color-text-primary)' }}>When statements arrive:</strong> upload them via the main wizard uploader — the AI
          classifier routes them automatically.  Parsed platforms auto-flip the checkboxes and status pills above, and
          reconciliation against 1065 + bank deposits runs hands-off at the end of the upload.
        </div>
      </div>
    </div>
  );
}
